import React, { useState, useRef, useEffect } from 'react';
import { Form, Input, Button, Typography, Alert, Divider, Modal, message } from 'antd';
import { LockOutlined, UserOutlined, EyeInvisibleOutlined, EyeTwoTone, ThunderboltOutlined, CodeOutlined } from '@ant-design/icons';
import { useMsal } from '@azure/msal-react';
import { useAuth } from '../stores/auth-store';
import './AccessGateLocal.css';

const { Title, Paragraph, Text } = Typography;

function AccessGateLocal() {
  const { login, loginWithMicrosoft, completePasswordChange, authError, isLoading } = useAuth();
  const { instance: msalInstance } = useMsal();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [localError, setLocalError] = useState('');
  const [signingIn, setSigningIn] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);
  const usernameRef = useRef(null);

  const [forceChangeVisible, setForceChangeVisible] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [changeError, setChangeError] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    if (usernameRef.current) usernameRef.current.focus();
  }, []);

  const handleMicrosoftLogin = async () => {
    if (ssoLoading) return;
    setLocalError('');
    setSsoLoading(true);
    try {
      await loginWithMicrosoft(msalInstance);
    } catch (err) {
      setLocalError(err?.message || 'Microsoft login failed');
      setSsoLoading(false);
    }
  };

  const handleSignIn = async () => {
    if (isLoading || signingIn) return;
    if (!username.trim()) { setLocalError('Username is required.'); return; }
    if (!password.trim()) { setLocalError('Password is required.'); return; }
    setLocalError('');
    setSigningIn(true);
    try {
      const result = await login(username.trim(), password);
      if (!result.success) {
        if (result.forcePasswordChange) {
          setTempToken(result.tempToken);
          setForceChangeVisible(true);
        } else if (result.error) {
          setLocalError(result.error);
        }
      }
    } finally {
      setSigningIn(false);
    }
  };

  const handleForcePasswordChange = async () => {
    setChangeError('');
    if (!newPassword || !confirmNewPassword) { setChangeError('All fields are required.'); return; }
    if (newPassword !== confirmNewPassword) { setChangeError('Passwords do not match.'); return; }
    if (newPassword.length < 8) { setChangeError('Password must be at least 8 characters.'); return; }
    if (!/[A-Z]/.test(newPassword)) { setChangeError('Password must contain an uppercase letter.'); return; }
    if (!/[a-z]/.test(newPassword)) { setChangeError('Password must contain a lowercase letter.'); return; }
    if (!/[0-9]/.test(newPassword)) { setChangeError('Password must contain a digit.'); return; }

    setChangingPassword(true);
    try {
      const result = await completePasswordChange(tempToken, newPassword);
      if (result.success) {
        message.success('Password changed successfully!');
        setForceChangeVisible(false);
      } else {
        setChangeError(result.error || 'Password change failed');
      }
    } finally {
      setChangingPassword(false);
    }
  };

  const displayError = localError || authError || '';

  return (
    <div className="access-gate-local">
      {/* Brand Panel - Left */}
      <div className="local-brand-panel">
        <div className="local-brand-visual">
          <svg viewBox="0 0 400 600" preserveAspectRatio="xMidYMid slice">
            <defs>
              <linearGradient id="localTeal" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#005F73" stopOpacity="0.6" />
                <stop offset="100%" stopColor="#0A9396" stopOpacity="0.4" />
              </linearGradient>
              <linearGradient id="localCyan" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#0A9396" stopOpacity="0.6" />
                <stop offset="100%" stopColor="#94D2BD" stopOpacity="0.4" />
              </linearGradient>
              <filter id="localGlow">
                <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                <feMerge>
                  <feMergeNode in="coloredBlur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
            </defs>
            <path className="local-data-line" d="M-50,100 Q100,200 200,150 T400,250" stroke="url(#localTeal)" strokeWidth="2" fill="none" filter="url(#localGlow)" />
            <path className="local-data-line" d="M-20,300 Q150,350 250,280 T450,400" stroke="url(#localCyan)" strokeWidth="2" fill="none" filter="url(#localGlow)" />
            <path className="local-data-line" d="M-80,450 Q80,500 220,420 T480,550" stroke="url(#localTeal)" strokeWidth="2" fill="none" filter="url(#localGlow)" />
            <path className="local-data-line" d="M-100,200 Q120,280 280,220 T500,350" stroke="url(#localCyan)" strokeWidth="2" fill="none" filter="url(#localGlow)" />
          </svg>
        </div>

        <div className="local-brand-content">
          <div className="local-brand-icon">
            <ThunderboltOutlined style={{ fontSize: '40px', color: '#FFFFFF' }} />
          </div>
          <Title level={2} className="local-brand-title">Next-Gen Platform Hub</Title>
          <Paragraph className="local-brand-tagline">Intelligent Platform Operations</Paragraph>
        </div>

        <div className="local-brand-footer">
          <Text style={{ fontSize: '11px', color: 'rgba(233, 216, 166, 0.5)' }}>
            Local Development Environment
          </Text>
        </div>
      </div>

      {/* Login Panel - Right */}
      <div className="local-login-panel">
        <div className="local-login-form">
          <div className="local-dev-badge">
            <CodeOutlined /> LOCAL DEV MODE
          </div>

          <h1 className="local-login-title">Sign In</h1>
          <p className="local-login-subtitle">Use your local development credentials</p>

          {displayError && (
            <Alert type="error" showIcon message={displayError}
              style={{ marginBottom: 16, borderRadius: 8 }}
              closable onClose={() => setLocalError('')} />
          )}

          <Form layout="vertical" onFinish={handleSignIn}>
            <Form.Item label="Username or Email">
              <Input
                ref={usernameRef}
                prefix={<UserOutlined />}
                value={username}
                onChange={e => { setUsername(e.target.value); setLocalError(''); }}
                onPressEnter={handleSignIn}
                placeholder="Enter username or email"
                size="large"
                disabled={signingIn}
                className="local-login-input"
                allowClear
              />
            </Form.Item>
            <Form.Item label="Password">
              <Input.Password
                prefix={<LockOutlined />}
                value={password}
                onChange={e => { setPassword(e.target.value); setLocalError(''); }}
                onPressEnter={handleSignIn}
                placeholder="Enter your password"
                size="large"
                disabled={signingIn}
                className="local-login-input"
                iconRender={v => v ? <EyeTwoTone /> : <EyeInvisibleOutlined />}
              />
            </Form.Item>
            <Button
              type="primary"
              size="large"
              block
              onClick={handleSignIn}
              disabled={!username.trim() || !password.trim()}
              loading={signingIn}
              className="local-login-btn"
            >
              {signingIn ? 'Signing in...' : 'Sign In'}
            </Button>

            <Divider plain style={{ margin: '16px 0 12px', fontSize: 12, color: 'rgba(0,0,0,0.35)' }}>OR</Divider>

            <Button
              size="large"
              block
              onClick={handleMicrosoftLogin}
              loading={ssoLoading}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                borderRadius: 8,
                border: '1px solid #d9d9d9',
                background: '#fff',
                fontWeight: 500,
              }}
            >
              <svg width="18" height="18" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
                <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
              </svg>
              {ssoLoading ? 'Connecting...' : 'Sign in with Microsoft Entra'}
            </Button>
          </Form>
        </div>
      </div>

      <Modal
        title="Set New Password"
        open={forceChangeVisible}
        zIndex={10000}
        onCancel={() => setForceChangeVisible(false)}
        onOk={handleForcePasswordChange}
        okText="Set Password"
        confirmLoading={changingPassword}
        closable={false}
        maskClosable={false}
      >
        <p>Your account requires a password change before you can continue.</p>
        {changeError && <Alert type="error" message={changeError} style={{ marginBottom: 12 }} showIcon />}
        <Form layout="vertical">
          <Form.Item label="New Password" required>
            <Input.Password
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              iconRender={v => v ? <EyeTwoTone /> : <EyeInvisibleOutlined />}
            />
          </Form.Item>
          <Form.Item label="Confirm Password" required>
            <Input.Password
              value={confirmNewPassword}
              onChange={e => setConfirmNewPassword(e.target.value)}
              placeholder="Confirm new password"
              onPressEnter={handleForcePasswordChange}
              iconRender={v => v ? <EyeTwoTone /> : <EyeInvisibleOutlined />}
            />
          </Form.Item>
        </Form>
        <Text type="secondary">Must be 8+ characters with uppercase, lowercase, and digit.</Text>
      </Modal>
    </div>
  );
}

export default AccessGateLocal;
